import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, formatINR, formatApiErrorDetail } from "@/lib/api";

const STATUSES = ["pending", "confirmed", "live", "completed", "cancelled"];

const EMPTY = {
  client_name: "", client_email: "", client_company: "", brand: "",
  cities: "", formats: "", budget: 1000000, duration_weeks: 4, start_date: "", notes: "",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function createOrder(e) {
    e.preventDefault();
    if (!form.client_name || !form.client_email || !form.brand) {
      toast.error("Client name, email and brand required.");
      return;
    }
    try {
      await api.post("/admin/orders", {
        ...form,
        cities: form.cities.split(",").map((s) => s.trim()).filter(Boolean),
        formats: form.formats.split(",").map((s) => s.trim()).filter(Boolean),
        budget: Number(form.budget),
        duration_weeks: Number(form.duration_weeks),
      });
      toast.success("Order created");
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function updateStatus(id, status) {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function deleteOrder(id) {
    if (!window.confirm("Delete this order?")) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success("Order deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="overline">Console · Orders</div>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl tracking-tighter">Orders</h1>
          <p className="mt-1 text-neutral-400 text-sm">Booked campaigns, status and revenue pipeline.</p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="btn-primary text-sm" data-testid="orders-new-btn">
          <Plus size={14} /> {showForm ? "Close" : "New order"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={createOrder} className="mt-6 surface p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="orders-form">
          <Input label="Client name *" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} />
          <Input label="Client email *" type="email" value={form.client_email} onChange={(v) => setForm({ ...form, client_email: v })} />
          <Input label="Client company" value={form.client_company} onChange={(v) => setForm({ ...form, client_company: v })} />
          <Input label="Brand *" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} />
          <Input label="Cities (comma separated)" value={form.cities} onChange={(v) => setForm({ ...form, cities: v })} />
          <Input label="Formats (comma separated)" value={form.formats} onChange={(v) => setForm({ ...form, formats: v })} />
          <Input label="Budget (INR)" type="number" value={form.budget} onChange={(v) => setForm({ ...form, budget: v })} />
          <Input label="Duration (weeks)" type="number" value={form.duration_weeks} onChange={(v) => setForm({ ...form, duration_weeks: v })} />
          <Input label="Start date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
          <Input label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
          <div className="md:col-span-2">
            <button type="submit" className="btn-primary">Create order</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="mt-8 text-neutral-500">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="mt-8 surface p-10 text-center text-neutral-500">
          No orders yet. Use the <em>New order</em> button to create one.
        </div>
      ) : (
        <div className="mt-8 surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-left text-[11px] uppercase tracking-[0.18em] text-neutral-500">
              <tr>
                <th className="p-4">Client / brand</th>
                <th className="p-4">Cities / formats</th>
                <th className="p-4">Budget</th>
                <th className="p-4">Duration</th>
                <th className="p-4">Status</th>
                <th className="p-4 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4 align-top">
                    <div className="font-medium">{o.brand}</div>
                    <div className="text-xs text-neutral-400 mt-0.5">{o.client_name} · {o.client_email}</div>
                    {o.client_company && <div className="text-xs text-neutral-500 mt-0.5">{o.client_company}</div>}
                  </td>
                  <td className="p-4 align-top max-w-xs">
                    <div className="text-xs text-neutral-300">{o.cities.join(", ") || "—"}</div>
                    <div className="text-xs text-neutral-500 mt-1">{o.formats.join(", ") || "—"}</div>
                  </td>
                  <td className="p-4 align-top whitespace-nowrap text-amber-500">{formatINR(o.budget)}</td>
                  <td className="p-4 align-top whitespace-nowrap">{o.duration_weeks}w{o.start_date ? ` · ${o.start_date}` : ""}</td>
                  <td className="p-4 align-top">
                    <select value={o.status} onChange={(e) => updateStatus(o.id, e.target.value)} className="max-w-[150px]">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-4 align-top">
                    <button onClick={() => deleteOrder(o.id)} className="text-neutral-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
